import { useEffect } from "react";
import { Toaster } from "@/components/ui/sonner";
import useReveal from "@/hooks/useReveal";
import { setPageSEO } from "@/lib/seo";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import WhatsAppFloat from "@/components/landing/WhatsAppFloat";
import SubpageHero from "@/components/landing/SubpageHero";
import WhatThisMeans from "@/components/landing/WhatThisMeans";
import FinalCTA from "@/components/landing/FinalCTA";
import { Users, GitBranch, Clock, Heart, Target, BarChart3, Shield } from "lucide-react";

const CHALLENGES = [
  {
    icon: Heart,
    title: "Lo familiar se mezcla con lo empresarial",
    text: "Las decisiones del negocio se ven influenciadas por dinámicas familiares. Emociones, lealtades y relaciones personales interfieren donde debería haber criterio estratégico.",
  },
  {
    icon: GitBranch,
    title: "Roles que se superponen",
    text: "El padre es CEO y también está en operaciones. El hijo está en ventas pero toma decisiones financieras. La indefinición de roles genera fricción constante y tareas duplicadas.",
  },
  {
    icon: Clock,
    title: "Sucesión sin estructura",
    text: "No hay un plan claro para el futuro del liderazgo. La conversación sobre la transición se posterga y la incertidumbre afecta a toda la organización.",
  },
  {
    icon: Users,
    title: "Accountability que se vuelve personal",
    text: "Exigir resultados a un familiar se siente como un ataque personal. EOS introduce métricas objetivas que despersonalizan el desempeño y permiten conversaciones difíciles con claridad.",
  },
];

const SOLUTIONS = [
  {
    icon: Target,
    num: "01",
    title: "Roles claros independientes de los lazos familiares",
    text: "El Accountability Chart de EOS define quién hace qué en la empresa, sin importar el vínculo familiar. Cada función tiene un responsable. El negocio y la familia se ordenan por separado.",
  },
  {
    icon: BarChart3,
    num: "02",
    title: "Métricas que hablan por sí solas",
    text: "El Scorecard semanal de EOS convierte el desempeño en números. Los datos no tienen apellido — permiten conversaciones honestas basadas en hechos, no en percepciones.",
  },
  {
    icon: Shield,
    num: "03",
    title: "Visión compartida que trasciende a la familia",
    text: "EOS alinea a toda la organización alrededor de una Visión a 10 años. Esto da contexto a la sucesión, al crecimiento y a las decisiones estratégicas de largo plazo.",
  },
];

export default function EOSEmpresasFamiliares() {
  useReveal();

  useEffect(() => {
    setPageSEO({
      title: "EOS para Empresas Familiares | Carlos Garcín Implementador EOS Chile",
      description: "EOS ayuda a empresas familiares a separar roles, generar accountability y escalar sin que todo dependa de la familia. Carlos Garcín, Implementador EOS.",
      path: "/eos-empresas-familiares",
      keywords: "EOS empresas familiares, sucesión empresa familiar, gobierno corporativo familiar Chile",
    });
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Header />
      <main>
        <SubpageHero
          eyebrow="EOS para Empresas Familiares"
          title="Crecer en familia es posible. Con la estructura correcta."
          subtitle="Las empresas familiares tienen una fortaleza enorme: compromiso, historia y valores sólidos. EOS aporta la estructura que permite que ese compromiso se traduzca en crecimiento sostenido y ordenado."
          withCta
        />

        {/* Desafíos de empresa familiar */}
        <section data-testid="family-challenges" className="section-padding bg-slate-50/60">
          <div className="container-eos">
            <div className="max-w-3xl reveal">
              <span className="eyebrow">El diagnóstico</span>
              <h2 className="mt-3 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-navy-800 leading-tight">
                Los desafíos únicos{" "}
                <span className="text-[#F26522]">de la empresa familiar</span>
              </h2>
              <p className="mt-5 text-lg text-slate-600 max-w-2xl">
                Cuatro fricciones que aparecen en casi toda empresa familiar que crece — y que EOS ayuda a resolver con claridad y sin dramas.
              </p>
            </div>
            <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-5 lg:gap-7">
              {CHALLENGES.map((c, i) => (
                <article
                  key={i}
                  data-testid={`family-challenge-${i + 1}`}
                  className="card-soft p-6 lg:p-7 reveal"
                  style={{ transitionDelay: `${i * 80}ms` }}
                >
                  <div className="w-10 h-10 rounded-lg bg-[#F26522]/10 flex items-center justify-center">
                    <c.icon className="w-5 h-5 text-[#F26522]" strokeWidth={1.7} />
                  </div>
                  <h3 className="mt-5 font-display font-bold text-lg text-navy-800 leading-snug">{c.title}</h3>
                  <p className="mt-2 text-slate-600 leading-relaxed">{c.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Cómo EOS los resuelve */}
        <section data-testid="family-solutions" className="section-padding bg-white">
          <div className="container-eos">
            <div className="max-w-3xl reveal">
              <span className="eyebrow">La solución EOS</span>
              <h2 className="mt-3 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-navy-800 leading-tight">
                EOS da claridad{" "}
                <span className="text-[#F26522]">donde antes había confusión</span>
              </h2>
              <p className="mt-5 text-lg text-slate-600 max-w-2xl">
                Carlos Garcín ha acompañado empresas familiares en Chile en el proceso de ordenar sus estructuras y crecer con disciplina, sin perder su esencia.
              </p>
            </div>
            <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
              {SOLUTIONS.map((s, idx) => (
                <article
                  key={idx}
                  data-testid={`family-solution-${idx + 1}`}
                  className="reveal relative rounded-2xl border border-slate-200 bg-white p-8 transition-all duration-300 hover:border-[#F26522]/40 hover:shadow-xl hover:-translate-y-1 overflow-hidden"
                  style={{ transitionDelay: `${idx * 90}ms` }}
                >
                  <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-[#F26522] to-[#F26522]/40" />
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-lg bg-navy-800 flex items-center justify-center shadow-md">
                      <s.icon className="w-5 h-5 text-white" strokeWidth={1.7} />
                    </div>
                    <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#F26522]">
                      {s.num} / 03
                    </span>
                  </div>
                  <h3 className="mt-6 font-display font-extrabold text-2xl text-navy-800">{s.title}</h3>
                  <p className="mt-3 text-slate-600 leading-relaxed">{s.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <WhatThisMeans />
        <FinalCTA />
      </main>
      <Footer />
      <WhatsAppFloat />
      <Toaster position="top-right" />
    </>
  );
}
